import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Image as ImageIcon, Loader2, Lock, Quote, Unlock, Sparkles, SmilePlus, Trash2, UserPlus, Video as VideoIcon, Workflow, Wrench, X } from 'lucide-react';
import {
  REACTIONS, agoLabel, canChangeIdea, canDeleteIdea, canTickIdea, reactionsOf, tagOf, toneOfPerson, wordCount,
  type Idea, type WorkspacePerson,
} from '../../utils/ideas';
import { CARD_TOOLS, formatCost, formatCostShort, type CardTool } from '../../utils/textModel';
import { generatorOf, parsePromptFile, type PromptPaste, type PromptToCanvas } from '../../utils/promptWriter';
import { shortModelLabel } from '../nodes/composer/modelLabels';
import { Avatar, BoardCard, ScriptText, errorText, useCloseOnOutside } from './parts';
import { PromptCardBody } from './PromptCardBody';

interface IdeaCardProps {
  idea: Idea;
  userId: string | null;
  people: WorkspacePerson[];
  /** May change the workspace (edit access) */
  canEditWorkspace: boolean;
  /** A model is there for the writing tools */
  toolsAvailable: boolean;
  /** Its name, for the menu */
  toolsModel?: string | null;
  /** A tool is working on this card */
  working: CardTool | 'name' | 'fix' | null;
  now: number;
  /** Opens in writing at once (a card just made), and once */
  autoEdit?: boolean;
  onAutoEdited?: () => void;
  /** Lit up: what a link asked for */
  highlighted?: boolean;
  onSaveText: (text: string) => Promise<void>;
  onSaveName: (name: string) => Promise<void>;
  /** A model names the card */
  onNameIt: () => Promise<void>;
  onReact: (emoji: string) => Promise<void>;
  onKeep: (locked: boolean) => Promise<void>;
  onGive: (personId: string | null) => Promise<void>;
  onTick: (done: boolean) => Promise<void>;
  onTool: (tool: CardTool) => Promise<void>;
  /** A prompt card's paste taken to the canvas as a node; absent where there is no canvas at hand */
  onSendToCanvas?: (card: PromptToCanvas) => Promise<void> | void;
  /** A prompt card that was run and came out wrong: the model finds the cause and rewrites it as a new card */
  onFix?: (wentWrong: string) => Promise<void>;
  onDelete: () => Promise<void>;
  onProblem: (message: string) => void;
}

type Menu = 'react' | 'give' | 'tools' | null;

/** A text area that is as tall as what is in it */
const fitHeight = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = '0px';
  el.style.height = `${Math.max(96, el.scrollHeight)}px`;
};

/**
 * One card of the board, in the frame of a node card: whoever wrote it and its name in the head
 * (in the writer's colour), its writing (changed in place), the reactions on it, when it was
 * written, whether it is kept, and who it was given to. The tools show while it is pointed at.
 */
export const IdeaCard: React.FC<IdeaCardProps> = ({
  idea, userId, people, canEditWorkspace, toolsAvailable, toolsModel = null, working, now, autoEdit = false, onAutoEdited, highlighted = false,
  onSaveText, onSaveName, onNameIt, onReact, onKeep, onGive, onTick, onTool, onSendToCanvas, onFix, onDelete, onProblem,
}) => {
  const [draft, setDraft] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  /** What came out wrong, being written; null while the fix form is closed */
  const [fixing, setFixing] = useState<string | null>(null);
  const [menu, setMenu] = useState<Menu>(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const closeMenu = useCallback(() => setMenu(null), []);
  const menuRef = useCloseOnOutside<HTMLDivElement>(menu !== null, closeMenu);

  const mayChange = canChangeIdea(idea, userId, canEditWorkspace, people);
  const mayDelete = canDeleteIdea(idea, userId, canEditWorkspace, people);
  const mayTick = canTickIdea(idea, userId, canEditWorkspace, people);
  const mayReact = !!userId;
  const author = people.find(person => person.id === idea.createdBy);
  const assignee = idea.assigneeId ? people.find(person => person.id === idea.assigneeId) : undefined;
  const tone = toneOfPerson(people, idea.createdBy);
  const reactions = reactionsOf(idea, userId);
  const words = wordCount(idea.text);
  const tag = tagOf(idea);
  const cost = idea.madeBy?.usd ?? null;
  // A prompt card: writing in the generation prompt writer's file format, shown as the file it is
  const file = useMemo(() => parsePromptFile(idea.text), [idea.text]);
  const generator = idea.promptFor?.model ? generatorOf(idea.promptFor.node, idea.promptFor.model) : null;
  const toCanvas = (paste: PromptPaste): PromptToCanvas => ({
    node: idea.promptFor?.node ?? 'video',
    model: idea.promptFor?.model ?? '',
    ...(idea.promptFor?.duration ? { duration: idea.promptFor.duration } : {}),
    ...(idea.promptFor?.aspectRatio ? { aspectRatio: idea.promptFor.aspectRatio } : {}),
    title: [idea.name || file?.title || tag || 'Prompt', paste.label].filter(Boolean).join(' · ').slice(0, 60),
    prompt: paste.text,
  });

  const commitName = () => {
    const name = (nameDraft ?? '').trim();
    setNameDraft(null);
    if (name === idea.name) return;
    void onSaveName(name).catch((e) => onProblem(e instanceof Error && e.message ? e.message : 'The name could not be saved.'));
  };

  useEffect(() => {
    if (autoEdit && mayChange) { setDraft(idea.text); onAutoEdited?.(); }
    // Once: the card just made
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEdit]);
  useEffect(() => { if (draft !== null) { fitHeight(textRef.current); textRef.current?.focus(); } }, [draft !== null]);
  useEffect(() => { fitHeight(textRef.current); }, [draft]);

  const startEditing = () => { if (mayChange && draft === null) setDraft(idea.text); };
  const stopEditing = async (keep: boolean) => {
    const text = draft;
    setDraft(null);
    if (!keep || text === null) return;
    const trimmed = text.trim();
    // A card left empty stays on the board, saying nothing is written yet, until it is deleted or written in
    if (trimmed === idea.text.trim()) return;
    setSaving(true);
    try { await onSaveText(text); } catch (e) { onProblem(e instanceof Error ? e.message : 'The card could not be saved.'); } finally { setSaving(false); }
  };
  const act = (run: () => Promise<void>, fallback: string) => { setMenu(null); void run().catch((e) => onProblem(e instanceof Error && e.message ? e.message : fallback)); };

  // The head: who wrote it, its name
  const title = nameDraft !== null ? (
    <input
      type="text"
      value={nameDraft}
      autoFocus
      maxLength={60}
      aria-label="Name"
      placeholder="A name of a few words"
      onChange={(e) => setNameDraft(e.target.value)}
      onBlur={commitName}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitName(); } if (e.key === 'Escape') { e.preventDefault(); setNameDraft(null); } }}
      className="ns-field ns-field-sm min-w-0 flex-1 h-7 py-0"
      data-card-name-field
    />
  ) : tag ? (
    <button
      type="button"
      onClick={() => { if (mayChange) setNameDraft(idea.name || tag); }}
      disabled={!mayChange}
      title={mayChange ? (idea.name ? 'Rename' : 'Named after its first line; click to give it a name') : undefined}
      className="canvas-node-title text-left min-w-0 flex-1"
      data-card-name={idea.name ? 'given' : 'first-line'}
    >
      {tag}
    </button>
  ) : mayChange ? (
    <button type="button" onClick={() => setNameDraft('')} className="canvas-node-title text-left min-w-0 flex-1 opacity-60" title="Give this card a name to know it by" data-card-name="none">
      Name it
    </button>
  ) : (
    <span className="canvas-node-title opacity-60" data-card-name="none">Untitled</span>
  );

  const tools = userId && !confirmDelete ? (
    <>
      {working === 'name' && <Loader2 size={12} className="animate-spin text-ink-faint mr-1" aria-label="Naming" />}
      {mayReact && (
        <button type="button" onClick={() => setMenu(menu === 'react' ? null : 'react')} aria-expanded={menu === 'react'} className="ns-icon-btn ns-icon-btn-xs node-reveal" aria-label="React" title="React to this">
          <SmilePlus />
        </button>
      )}
      {canEditWorkspace && (idea.locked ? (idea.lockedBy === userId || mayChange) : true) && (
        <button type="button" onClick={() => act(() => onKeep(!idea.locked), 'The card could not be kept.')} className={`ns-icon-btn ns-icon-btn-xs ${idea.locked ? '' : 'node-reveal'}`} aria-pressed={idea.locked} aria-label={idea.locked ? 'Let this go' : 'Keep this'} title={idea.locked ? 'Let it go: anyone may change it again' : 'Keep it: nobody else changes or deletes it'}>
          {idea.locked ? <Unlock /> : <Lock />}
        </button>
      )}
      {mayChange && (
        <button type="button" onClick={() => setMenu(menu === 'give' ? null : 'give')} aria-expanded={menu === 'give'} className="ns-icon-btn ns-icon-btn-xs node-reveal" aria-label="Give to someone" title="Give it to one of the people of this workspace, as a task">
          <UserPlus />
        </button>
      )}
      {canEditWorkspace && (
        <button type="button" onClick={() => setMenu(menu === 'tools' ? null : 'tools')} aria-expanded={menu === 'tools'} disabled={working !== null} className="ns-icon-btn ns-icon-btn-xs node-reveal" aria-label="Writing tools" title={toolsAvailable ? 'Expand, shorten, rewrite, or three takes on it, as new cards' : 'Writing tools need a Venice, Google or OpenAI key (Settings)'}>
          {working ? <Loader2 className="animate-spin" /> : <Sparkles />}
        </button>
      )}
      {mayDelete && (
        <button type="button" onClick={() => setConfirmDelete(true)} className="ns-icon-btn ns-icon-btn-xs ns-icon-btn-danger" aria-label="Delete this card" title="Delete this card">
          <Trash2 />
        </button>
      )}
    </>
  ) : null;

  return (
    <BoardCard
      id={idea.id}
      tone={tone}
      mark={<Avatar member={author} name={idea.creatorName} size={24} />}
      title={title}
      tools={tools}
      highlighted={highlighted}
      data={{ 'data-kept': idea.locked ? 'true' : undefined, 'data-done': idea.done ? 'true' : undefined, 'data-source': idea.source }}
    >
      {menu && (
        <div ref={menuRef} className="absolute right-2 top-10 z-20 ns-menu" role="menu" data-card-menu={menu}>
          {menu === 'react' && (
            <div className="flex items-center gap-0.5 px-1">
              {REACTIONS.map(emoji => {
                const mine = reactions.find(r => r.emoji === emoji)?.mine;
                return (
                  <button key={emoji} type="button" role="menuitemcheckbox" aria-checked={!!mine} onClick={() => act(() => onReact(emoji), 'The reaction did not go through.')} className={`w-8 h-8 shrink-0 grid place-items-center rounded-full text-lg hover:bg-lavender-soft ${mine ? 'bg-lavender-soft' : ''}`} aria-label={`React with ${emoji}`}>
                    {emoji}
                  </button>
                );
              })}
            </div>
          )}
          {menu === 'give' && (
            <>
              <div className="ns-menu-label">Give it to</div>
              {people.map(person => (
                <button key={person.id} type="button" role="menuitemradio" aria-checked={idea.assigneeId === person.id} onClick={() => act(() => onGive(person.id), 'The card could not be given.')} className="ns-menu-item">
                  <Avatar member={person} size={20} /> {person.id === userId ? `${person.name} (you)` : person.name}
                </button>
              ))}
              {idea.assigneeId && (
                <>
                  <div className="ns-menu-sep" />
                  <button type="button" role="menuitem" onClick={() => act(() => onGive(null), 'The card could not be taken back.')} className="ns-menu-item"><X /> Nobody</button>
                </>
              )}
            </>
          )}
          {menu === 'tools' && (
            <>
              <button type="button" role="menuitem" disabled={!toolsAvailable || !idea.text.trim()} title="A name of a few words for this card, from the model" onClick={() => act(() => onNameIt(), 'The card could not be named.')} className="ns-menu-item" data-tool="name">
                <Sparkles /> Name it
              </button>
              {file && onFix && (
                <button type="button" role="menuitem" disabled={!toolsAvailable} title="Say what came out wrong when this prompt was run; the model finds the cause and rewrites the prompt as a new card, one version on" onClick={() => { setMenu(null); setFixing(''); }} className="ns-menu-item" data-tool="fix">
                  <Wrench /> Fix what went wrong
                </button>
              )}
              <div className="ns-menu-sep" />
              <div className="ns-menu-label">As new cards{toolsModel ? ` · ${toolsModel}` : ''}</div>
              {CARD_TOOLS.map(tool => (
                <button key={tool.id} type="button" role="menuitem" disabled={!toolsAvailable || !idea.text.trim()} title={tool.hint} onClick={() => act(() => onTool(tool.id), 'The writing tool did not answer.')} className="ns-menu-item">
                  <Sparkles /> {tool.label}
                </button>
              ))}
              {!toolsAvailable && <p className="px-2.5 py-1.5 text-xs text-ink-faint max-w-[220px]">Needs a Venice, Google or OpenAI key, added in Settings.</p>}
            </>
          )}
        </div>
      )}

      {confirmDelete && (
        <div className="ns-note m-3" data-tone="danger" data-confirm-delete>
          <span className="flex-1">Delete this card?</span>
          <button type="button" onClick={() => { setConfirmDelete(false); act(() => onDelete(), 'The card could not be deleted.'); }} className="ns-btn ns-btn-xs ns-btn-danger ns-btn-flat">Delete</button>
          <button type="button" onClick={() => setConfirmDelete(false)} className="ns-btn ns-btn-xs ns-btn-soft ns-btn-flat">Keep</button>
        </div>
      )}

      {/* What a model's card was asked with, to look back on */}
      {idea.prompt && (
        <details className="mx-4 mt-3 group/prompt" data-card-prompt>
          <summary className="ns-t-meta inline-flex items-center gap-1 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden hover:text-ink">
            <Quote size={12} aria-hidden="true" /> The prompt
          </summary>
          <p className="mt-1 px-3 py-2 rounded-lg bg-ink/[0.04] text-xs leading-5 text-ink-soft whitespace-pre-wrap [overflow-wrap:anywhere]" data-card-prompt-text>{idea.prompt}</p>
        </details>
      )}

      {/* The writing */}
      <div className="px-5 pt-3 pb-3">
        {draft !== null ? (
          <textarea
            ref={textRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => void stopEditing(true)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { e.preventDefault(); void stopEditing(false); }
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); void stopEditing(true); }
            }}
            placeholder="Write the idea. A short first line names it. Ctrl+Enter when done."
            aria-label="Card text"
            className="ns-field ns-field-bare w-full resize-none font-serif text-[15px] leading-7 text-ink p-0"
            data-card-text-field
          />
        ) : (
          <div
            onClick={startEditing}
            onKeyDown={(e) => { if (e.key === 'Enter' && mayChange) { e.preventDefault(); startEditing(); } }}
            role={mayChange ? 'button' : undefined}
            tabIndex={mayChange ? 0 : undefined}
            aria-label={mayChange ? 'Change the text' : undefined}
            className={`rounded-lg -mx-1 px-1 ${mayChange ? 'cursor-text' : ''} ${idea.done ? 'opacity-60' : ''}`}
            data-card-text
          >
            {/* The first line is the head's name while the card has none of its own, so it is not said twice */}
            {file ? (
              <PromptCardBody file={file} promptFor={idea.promptFor} onSendToCanvas={onSendToCanvas ? (paste) => { void Promise.resolve(onSendToCanvas(toCanvas(paste))).catch((e) => onProblem(errorText(e, 'The node could not be made.'))); } : undefined} />
            ) : idea.text.trim() ? <ScriptText text={idea.text} skipFirstLine={!idea.name && tag !== null} /> : idea.name ? null : <p className="font-serif text-[15px] leading-7 text-ink-faint">Nothing written yet.</p>}
          </div>
        )}
      </div>

      {/* A prompt that was run and came out wrong: what, in the person's words; the fix is a new card */}
      {fixing !== null && (
        <form className="mx-4 mb-3 flex flex-col gap-2" data-fix-form onSubmit={(e) => { e.preventDefault(); const wentWrong = fixing.trim(); if (!wentWrong || !onFix) return; setFixing(null); act(() => onFix(wentWrong), 'The prompt could not be fixed.'); }} onPointerDown={(e) => e.stopPropagation()}>
          <textarea value={fixing} onChange={(e) => setFixing(e.target.value)} autoFocus rows={3} aria-label="What came out wrong" placeholder="What came out wrong? The fox was already in the first frame. There were two keys. The arm stretched across the room." className="ns-field w-full resize-none text-sm leading-6" />
          <div className="flex flex-wrap items-center gap-2">
            <button type="submit" disabled={!fixing.trim() || working !== null} className="ns-btn ns-btn-xs ns-btn-primary ns-btn-flat"><Wrench size={12} aria-hidden="true" /> Fix the prompt</button>
            <button type="button" onClick={() => setFixing(null)} className="ns-btn ns-btn-xs ns-btn-soft ns-btn-flat">Not now</button>
            <span className="ns-t-meta">The fix lands as a new card, one version on; this one stays.</span>
          </div>
        </form>
      )}

      {/* The reactions, and one more */}
      {(reactions.length > 0 || saving) && (
        <div className="flex flex-wrap items-center gap-1.5 px-4 pb-3" data-card-reactions>
          {reactions.map(({ emoji, count, mine }) => (
            <button
              key={emoji}
              type="button"
              disabled={!mayReact}
              aria-pressed={mine}
              onClick={() => act(() => onReact(emoji), 'The reaction did not go through.')}
              className={`ns-chip ns-chip-sm gap-1 ${mine ? 'ns-chip-lavender' : ''}`}
              title={`${count} ${count === 1 ? 'person' : 'people'}${mine ? ', you among them' : ''}`}
              data-reaction={emoji}
            >
              <span className="text-sm leading-none">{emoji}</span> {count}
            </button>
          ))}
          {saving && <span className="ns-t-meta flex items-center gap-1"><Loader2 size={12} className="animate-spin" aria-hidden="true" /> Saving</span>}
        </div>
      )}

      {/* Who and when; who it was given to */}
      <footer className="flex items-center gap-2 px-4 py-2.5 border-t border-ink/5 min-w-0">
        <span className="ns-t-meta truncate min-w-0" title={`${idea.creatorName}, ${agoLabel(idea.createdAt, now)}`}>
          {idea.createdBy === userId ? 'You' : idea.creatorName} · {agoLabel(idea.updatedAt, now)}{words > 0 ? ` · ${words} ${words === 1 ? 'word' : 'words'}` : ''}
        </span>
        {idea.source === 'ai' && (
          <span
            className="ns-chip ns-chip-sm shrink-0"
            title={idea.madeBy?.model === 'Claude' ? 'Written by Claude through the Noodle Studio connector'
              : idea.madeBy ? `Written by ${idea.madeBy.model}${cost !== null ? `: ${formatCost(cost)} of this card's share` : ', billed to your own key'}` : 'Written by a model'}
            data-card-cost={cost ?? undefined}
          >
            <Sparkles size={12} aria-hidden="true" /> {idea.madeBy?.model ? idea.madeBy.model.replace(/^Claude /, '') : 'AI'}{cost !== null ? ` · ${formatCostShort(cost)}` : ''}
          </span>
        )}
        {idea.source === 'node' && <span className="ns-chip ns-chip-sm shrink-0" title="Sent from a node on the canvas"><Workflow size={12} aria-hidden="true" /> Canvas</span>}
        {idea.promptFor && (
          <span
            className="ns-chip ns-chip-sm shrink-0"
            title={`A prompt for ${idea.promptFor.node === 'video' ? 'a Video' : 'an Image'} node${generator ? `, written for ${shortModelLabel(generator.label)}` : ''}${idea.promptFor.duration ? `, ${idea.promptFor.duration.replace(/s$/i, '')} seconds` : ''}${idea.promptFor.aspectRatio ? `, ${idea.promptFor.aspectRatio}` : ''}`}
            data-prompt-for={idea.promptFor.node}
          >
            {idea.promptFor.node === 'video' ? <VideoIcon size={12} aria-hidden="true" /> : <ImageIcon size={12} aria-hidden="true" />} {generator ? shortModelLabel(generator.label) : idea.promptFor.node === 'video' ? 'Video prompt' : 'Image prompt'}
          </span>
        )}
        {idea.locked && (
          <span className="ns-chip ns-chip-sm shrink-0" title={`Kept by ${idea.lockedByName || 'someone'}`} data-kept-chip>
            <Lock size={12} aria-hidden="true" /> Kept
          </span>
        )}
        <span className="ml-auto flex items-center gap-1.5 shrink-0">
          {idea.assigneeId && (
            <span className={`ns-chip ns-chip-sm gap-1.5 ${idea.done ? 'ns-chip-ok' : 'ns-chip-lavender'}`} title={idea.done ? `Done by ${idea.doneByName || idea.assigneeName}` : `Given to ${idea.assigneeName}`} data-given-to={idea.assigneeId}>
              <Avatar member={assignee} name={idea.assigneeName ?? undefined} size={20} />
              {idea.assigneeId === userId ? 'You' : idea.assigneeName}
            </span>
          )}
          {idea.assigneeId && (
            <button
              type="button"
              role="checkbox"
              aria-checked={idea.done}
              aria-label={idea.done ? 'Done' : 'Mark done'}
              disabled={!mayTick}
              onClick={() => act(() => onTick(!idea.done), 'The card could not be ticked.')}
              className={`ns-icon-btn ns-icon-btn-xs ${idea.done ? 'bg-mint text-ink hover:bg-mint' : 'ns-icon-btn-line'}`}
              title={idea.done ? `Done ${idea.doneAt ? agoLabel(idea.doneAt, now) : ''}` : 'Mark done'}
            >
              {idea.done ? <Check strokeWidth={3} /> : null}
            </button>
          )}
        </span>
      </footer>
    </BoardCard>
  );
};

export default IdeaCard;
